import { AppstoreAddOutlined } from '@ant-design/icons';
import { Layout } from 'antd';
import classNames from 'classnames';
import { useContext, useEffect, useMemo, useState } from 'react';

import { TagWithIcon } from 'app/components/commons/TagWithIcon/TagWithIcon';
import { DotIcon } from 'app/components/commons/icons/DotIcon';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import { TaskCenterContext } from 'app/context/TaskCenterContext/TaskCenterContext';
import { useBedCategories } from 'app/hooks/data/rooms/useBedCategories';
import { useRoomCategories } from 'app/hooks/data/rooms/useRoomCategories';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import type { ExtranetPackage } from 'app/typings/packages';
import { useTrackViewPage } from 'app/utils/track/amplitude';

import { EmptyState } from '../../commons/EmptyState/EmptyState';
import { ExtranetHeader } from '../../commons/ExtranetHeader/ExtranetHeader';

import './ExtranetPackages.scss';
import type { PackageDetailProps } from './_components/PackageModal/PackageDetail';
import { PackageDetail } from './_components/PackageModal/PackageDetail';
import { PackageModal } from './_components/PackageModal/PackageModal';
import { PackageModalTitle } from './_components/PackageModal/PackageModalTitle';
import { PackagesTabs } from './_components/PackageTabs/PackagesTabs';
import { PackageUnpublishedAlert } from './_components/PackageUnpublishedAlert';
import { TableRowExperiences } from './_components/TableRowExperiences';
import { TableRowRooms } from './_components/TableRowRooms';

const PAGE = 'Packages';

const PackagesInfo = () => {
  const { publishedPackages, getExperiencesForPackage, getRoomsForPackage } =
    useContext(LegacyHotelContext);

  const { setPage } = useContext(TaskCenterContext);

  const { data: bedCategories } = useBedCategories();
  const { data: roomCategories } = useRoomCategories();
  const { data: experienceCategories } = useExperiencesCategories();

  const [activePackage, setActivePackage] = useState<
    ExtranetPackage | undefined
  >(publishedPackages[0]);

  const experiences = activePackage
    ? getExperiencesForPackage(activePackage.id)
    : [];

  const rooms = getRoomsForPackage(activePackage?.id).sort(
    (a, b) => (a.hotelPriority ?? Infinity) - (b.hotelPriority ?? Infinity)
  );

  const addOnsList =
    experiences.filter(
      (item) => activePackage?.id && !item.included[activePackage.id]
    ) ?? [];

  const experiencesList =
    experiences
      .filter((item) => activePackage?.id && item.included[activePackage.id])
      .sort(
        (a, b) => (a.hotelPriority ?? Infinity) - (b.hotelPriority ?? Infinity)
      ) ?? [];

  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [activeDataModal, setActiveDataModal] = useState<
    PackageDetailProps | undefined
  >();

  useTrackViewPage({ page: 'packages' });

  useEffect(() => {
    setPage(PAGE);
  });

  useEffect(() => {
    if (!!publishedPackages) {
      setActivePackage(publishedPackages?.[0]);
    }
  }, [publishedPackages]);

  const items = useMemo(() => {
    if (!publishedPackages || publishedPackages.length === 0) {
      return [];
    }

    return publishedPackages.map((pkg) => ({
      label: pkg.published ? (
        <span key={pkg.id}>{`Package - ${pkg.aliasForHotel}`}</span>
      ) : (
        <span className="unpublished-package" key={pkg.id}>
          {`Package - ${pkg.aliasForHotel}`}
          <TagWithIcon
            text={pkg.published ? 'Published' : 'Unpublished'}
            icon={
              <DotIcon
                color={pkg.published ? '#28A745' : '#808080'}
                width={6}
                height={6}
              />
            }
            type={pkg.published ? 'success' : 'default'}
          />
        </span>
      ),
      key: pkg.slug,
    }));
  }, [publishedPackages]);

  const onChangeTabs = (slug: string) => {
    if (!publishedPackages) {
      return [];
    }

    setActivePackage(publishedPackages.find((pkg) => pkg.slug === slug));
  };

  if (!publishedPackages) {
    return <></>;
  }

  const hasPackage = publishedPackages.length > 0;

  const showExtranetHeader = () => {
    if (hasPackage) {
      return (
        <ExtranetHeader
          page={PAGE}
          withPackageButton={publishedPackages.length === 1}
          packageSlug={publishedPackages[0].slug}
        />
      );
    }

    return <ExtranetHeader page={PAGE} />;
  };

  return (
    <Layout className="extranet-table">
      {showExtranetHeader()}
      <div
        className={classNames(['extranet-package', { noContent: !hasPackage }])}
      >
        {hasPackage ? (
          <>
            {publishedPackages.length > 1 && (
              <PackagesTabs
                onChangeTabs={onChangeTabs}
                items={items}
                activeSlug={activePackage?.slug}
              />
            )}

            {!activePackage?.published && <PackageUnpublishedAlert />}

            <div className="extranet-package-container">
              <TableRowExperiences
                title="Experiences"
                isDayPackage={activePackage?.dayPackage}
                experiencesList={experiencesList}
                experienceCategories={experienceCategories}
                setActiveDataModal={setActiveDataModal}
                setIsDetailModalOpen={setIsDetailModalOpen}
              />

              <TableRowExperiences
                title="Add-ons"
                isDayPackage={activePackage?.dayPackage}
                experiencesList={addOnsList}
                experienceCategories={experienceCategories}
                setActiveDataModal={setActiveDataModal}
                setIsDetailModalOpen={setIsDetailModalOpen}
              />

              <TableRowRooms
                title={activePackage?.dayPackage ? 'Slots' : 'Rooms'}
                isDayPackage={activePackage?.dayPackage}
                rooms={rooms ?? []}
                roomCategories={roomCategories ?? []}
                bedCategories={bedCategories ?? []}
                setActiveDataModal={setActiveDataModal}
                setIsDetailModalOpen={setIsDetailModalOpen}
              />
            </div>
          </>
        ) : (
          <EmptyState
            title="No published packages"
            description="Please contact your Account Manager for more information."
            icon={<AppstoreAddOutlined />}
          />
        )}
      </div>
      <PackageModal
        isDetailModalOpen={isDetailModalOpen}
        setIsDetailModalOpen={setIsDetailModalOpen}
        title={activeDataModal && <PackageModalTitle {...activeDataModal} />}
      >
        {activeDataModal && <PackageDetail {...activeDataModal} />}
      </PackageModal>
    </Layout>
  );
};

export const ExtranetPackages = () => {
  const { hotelId } = useContext(HotelContext);

  if (!hotelId) {
    return <></>;
  }

  return <PackagesInfo />;
};
